import React, { useEffect, useState, useMemo, useCallback, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RouteComponentProps } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faEnvelope,
  faSpinner,
  faCheck,
  faTrash,
  faExternalLinkAlt,
  faCopy,
  faPaperPlane,
  faUserPlus,
  faClock,
  faInfoCircle,
  faPencilAlt,
  faSyncAlt,
  faCommentAlt,
  faPlusCircle,
} from "@fortawesome/free-solid-svg-icons";

import Container from "../../components/UI/Container/Container";
import Spinner from "../../components/UI/Spinner/Spinner";
import Button from "../../components/UI/Button/Button";
import ConfirmDialog from "../../components/UI/ConfirmDialog/ConfirmDialog";
import ModalContext, { defaultModal } from "../../components/UI/Modal/ModalContext";
import LeadService, {
  ILead,
  ILeadNote,
  IBulkConvertResult,
  LeadStatus,
  LEAD_STATUS_LABELS,
  buildLeadTimeline,
} from "../../services/LeadService";
import { IAppState, IServiceState } from "../../interfaces/state";
import * as actions from "../../store/actions";
import classes from "./Leads.module.scss";

const CONTACT_METHOD_LABELS: Record<string, string> = {
  PHONE: "Puhelu",
  PUHELU: "Puhelu",
  EMAIL: "Sähköposti",
  VISIT: "Käynti",
  OTHER: "Muu",
};

type ViewMode = "table" | "pipeline";

interface IProps extends RouteComponentProps {}

const pipelineColumns: { status: LeadStatus; label: string; color: string }[] = [
  { status: LeadStatus.NOT_CONTACTED, label: "Ei kontaktoitu", color: "#64748b" },
  { status: LeadStatus.CONTACTED, label: "Kontaktoitu", color: "#2563eb" },
  { status: LeadStatus.NOT_INTERESTED, label: "Ei kiinnosta", color: "#dc2626" },
  { status: LeadStatus.CONTRACT, label: "Sopimus", color: "#16a34a" },
];

const Leads: React.FC<IProps> = ({ history }) => {
  const modal = useContext(ModalContext);
  const [leads, setLeads] = useState<ILead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("table");
  const [statusFilter, setStatusFilter] = useState<LeadStatus | "ALL">("ALL");
  const [searchQuery, setSearchQuery] = useState("");

  // Drawer state
  const [selectedLead, setSelectedLead] = useState<ILead | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activityFilter, setActivityFilter] = useState<"ALL" | "NOTES" | "CHANGES">("ALL");
  const [notes, setNotes] = useState<ILeadNote[]>([]);
  const [newNote, setNewNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingNoteContent, setEditingNoteContent] = useState("");
  const [savingEditNote, setSavingEditNote] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [deletingLead, setDeletingLead] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<string[]>([]);

  // Bulk convert state
  const [selectedServiceToken, setSelectedServiceToken] = useState("");
  const [converting, setConverting] = useState(false);
  const [convertResult, setConvertResult] = useState<IBulkConvertResult | null>(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const dispatch = useDispatch();
  const { services } = useSelector<IAppState, IServiceState>(state => state.services);

  useEffect(() => {
    if (!services) {
      dispatch(actions.listServices());
    }
  }, [dispatch, services]);

  // ---- Load leads ----
  const fetchLeads = useCallback(async () => {
    try {
      setLoading(true);
      const data = await LeadService.listLeads();
      setLeads(data);
    } catch (err: any) {
      setError("Liidien lataus epäonnistui: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // ---- Filter & search ----
  const filteredLeads = useMemo(() => {
    let filtered = leads;
    if (statusFilter !== "ALL") {
      filtered = filtered.filter((l) => l.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (l) =>
          (l.placeName || "").toLowerCase().includes(q) ||
          (l.ytjName || "").toLowerCase().includes(q) ||
          (l.city || "").toLowerCase().includes(q) ||
          (l.businessId || "").toLowerCase().includes(q)
      );
    }
    
    // Sort descending by created date and time (newest first)
    const sorted = [...filtered].sort((a, b) => {
      const timeA = a.created ? new Date(a.created).getTime() : 0;
      const timeB = b.created ? new Date(b.created).getTime() : 0;
      return timeB - timeA;
    });

    return sorted;
  }, [leads, statusFilter, searchQuery]);

  // ---- Drawer open ----
  const openDrawer = useCallback(async (lead: ILead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
    setActivityFilter("ALL");
    setEditingNoteId(null);
    setEmailSent(false);
    setNewNote("");
    setLoadingNotes(true);
    try {
      const full = await LeadService.getLead(lead.id);
      setSelectedLead(full);
      setNotes(full.notes || []);
    } catch (err) {
      console.error("Error fetching lead details:", err);
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedLead(null);
    setNotes([]);
    setEditingNoteId(null);
  }, []);

  // ---- Status update ----
  const handleStatusChange = useCallback(
    async (status: LeadStatus) => {
      if (!selectedLead) return;
      setUpdatingStatus(true);
      try {
        const updated = await LeadService.updateLead(selectedLead.id, { status });
        // Refresh full lead to get newly generated AuditTrail
        const full = await LeadService.getLead(selectedLead.id);
        setSelectedLead(full);
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      } catch (err) {
        console.error("Error updating status:", err);
      } finally {
        setUpdatingStatus(false);
      }
    },
    [selectedLead]
  );

  // ---- Add note ----
  const handleAddNote = useCallback(async () => {
    if (!selectedLead || !newNote.trim()) return;
    setSavingNote(true);
    try {
      let serviceName: string | undefined = undefined;
      if (selectedLead.serviceToken && services) {
        const found = services.find((s) => s.serviceToken === selectedLead.serviceToken);
        if (found) serviceName = found.name;
      }
      const note = await LeadService.addLeadNote(
        selectedLead.id,
        newNote.trim(),
        selectedLead.serviceToken,
        serviceName
      );
      setNotes((prev) => [note, ...prev]);
      setNewNote("");
    } catch (err) {
      console.error("Error adding note:", err);
    } finally {
      setSavingNote(false);
    }
  }, [selectedLead, newNote, services]);

  // ---- Edit note ----
  const handleStartEditNote = useCallback((note: ILeadNote) => {
    setEditingNoteId(note.id);
    setEditingNoteContent(note.content);
  }, []);

  const handleCancelEditNote = useCallback(() => {
    setEditingNoteId(null);
    setEditingNoteContent("");
  }, []);

  const handleSaveEditNote = useCallback(async (noteId: string) => {
    if (!editingNoteContent.trim() || !selectedLead) return;
    setSavingEditNote(true);
    try {
      let serviceName: string | undefined = undefined;
      if (selectedLead.serviceToken && services) {
        const found = services.find((s) => s.serviceToken === selectedLead.serviceToken);
        if (found) serviceName = found.name;
      }
      const updated = await LeadService.updateLeadNote(
        noteId,
        editingNoteContent.trim(),
        selectedLead.serviceToken,
        serviceName
      );
      setNotes((prev) => prev.map((n) => (n.id === noteId ? updated : n)));
      setEditingNoteId(null);
      setEditingNoteContent("");
    } catch (err) {
      console.error("Error updating note:", err);
    } finally {
      setSavingEditNote(false);
    }
  }, [editingNoteContent, selectedLead, services]);

  // ---- Delete note ----
  const handleDeleteNote = useCallback((noteId: string) => {
    modal.setModal({
      ...defaultModal,
      isOpen: true,
      title: "Poista muistiinpano",
      content: (
        <ConfirmDialog
          body="Haluatko varmasti poistaa tämän muistiinpanon?"
          onConfirm={async () => {
            modal.setModal(defaultModal);
            try {
              await LeadService.deleteLeadNote(noteId);
              setNotes((prev) => prev.filter((n) => n.id !== noteId));
            } catch (err) {
              console.error("Error deleting note:", err);
            }
          }}
          onCancel={() => modal.setModal(defaultModal)}
        />
      ),
    });
  }, [modal]);

  // ---- Send email ----
  const handleSendEmail = useCallback(async () => {
    if (!selectedLead) return;
    setSendingEmail(true);
    try {
      const result = await LeadService.sendEmail(selectedLead.id);
      if (result.success) {
        setEmailSent(true);
        // Refresh full lead to get emailSentAt & audit trails
        const updated = await LeadService.getLead(selectedLead.id);
        setSelectedLead(updated);
        setLeads((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
      }
    } catch (err) {
      console.error("Error sending email:", err);
    } finally {
      setSendingEmail(false);
    }
  }, [selectedLead]);

  // ---- Delete lead ----
  const handleDelete = useCallback(async () => {
    if (!selectedLead) return;
    if (!window.confirm("Haluatko varmasti poistaa tämän liidin?")) return;
    setDeletingLead(true);
    try {
      await LeadService.deleteLead(selectedLead.id);
      setLeads((prev) => prev.filter((l) => l.id !== selectedLead.id));
      closeDrawer();
    } catch (err) {
      console.error("Error deleting lead:", err);
    } finally {
      setDeletingLead(false);
    }
  }, [selectedLead, closeDrawer]);

  // ---- Copy email to clipboard ----
  const handleCopyEmail = useCallback(() => {
    if (!selectedLead?.generatedEmailBody) return;
    const text = `${selectedLead.generatedEmailSubject || ""}\n\n${selectedLead.generatedEmailBody}`;
    navigator.clipboard.writeText(text);
  }, [selectedLead]);

  // ---- Timeline & Activity helpers ----
  const timelineItems = useMemo(() => {
    if (!selectedLead) return [];
    return buildLeadTimeline({ ...selectedLead, notes });
  }, [selectedLead, notes]);

  const filteredTimelineItems = useMemo(() => {
    if (activityFilter === "NOTES") {
      return timelineItems.filter((i) => i.type === "NOTE");
    }
    if (activityFilter === "CHANGES") {
      return timelineItems.filter((i) => i.type !== "NOTE");
    }
    return timelineItems;
  }, [timelineItems, activityFilter]);

  // Relative time helper
  const timeAgo = (dateStr?: string) => {
    if (!dateStr) return "";
    const diffMs = Date.now() - new Date(dateStr).getTime();
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return "Juuri nyt";
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin} min sitten`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours} t sitten`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Eilen";
    if (diffDays < 7) return `${diffDays} pv sitten`;
    return formatDate(dateStr);
  };

  // Initials helper
  const getInitials = (name?: string) => {
    if (!name) return "?";
    const parts = name.trim().split(" ");
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  // ---- Status badge helper ----
  const statusBadgeClass = (status: LeadStatus) => {
    switch (status) {
      case LeadStatus.NOT_CONTACTED: return classes.StatusNotContacted;
      case LeadStatus.CONTACTED: return classes.StatusContacted;
      case LeadStatus.NOT_INTERESTED: return classes.StatusNotInterested;
      case LeadStatus.CONTRACT: return classes.StatusContract;
      default: return classes.StatusNotContacted;
    }
  };

  // ---- Format date ----
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("fi-FI");
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleString("fi-FI");
    } catch {
      return dateStr;
    }
  };

  // ---- Pipeline helpers ----

  const leadsByStatus = useMemo(() => {
    const map: Record<string, ILead[]> = {};
    for (const col of pipelineColumns) {
      map[col.status] = leads.filter((l) => l.status === col.status);
    }
    return map;
  }, [leads]);

  // Search match helper for pipeline highlight
  const isSearchMatch = useCallback((lead: ILead) => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (lead.placeName || "").toLowerCase().includes(q) ||
      (lead.ytjName || "").toLowerCase().includes(q) ||
      (lead.city || "").toLowerCase().includes(q) ||
      (lead.businessId || "").toLowerCase().includes(q)
    );
  }, [searchQuery]);

  if (loading) {
    return (
      <Container>
        <Spinner />
      </Container>
    );
  }

  return (
    <Container>
      {/* Header */}
      <div className={classes.LeadsHeader}>
        <div>
          <h2>Liidit</h2>
          <p className={classes.Sub}>{leads.length} liidiä yhteensä</p>
        </div>
        <div className={classes.Actions}>
          <select
            value={selectedServiceToken}
            onChange={(e) => setSelectedServiceToken(e.target.value)}
            style={{ padding: "0.5rem", borderRadius: "6px", border: "1px solid #d1d5db", marginRight: "0.5rem" }}
          >
            <option value="">Valitse palvelu...</option>
            {(services || []).filter(s => !s.archived && s.serviceToken).map((svc) => (
              <option key={svc.id} value={svc.serviceToken}>{svc.name || svc.serviceToken}</option>
            ))}
          </select>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!selectedServiceToken || converting || selectedLeadIds.length === 0}
          >
            <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "0.4rem" }} />
            Tee valituista ({selectedLeadIds.length}) palveluntarjoajia
          </Button>
          <Button onClick={() => history.push("/onboarding")}>+ Uusi liidi</Button>
        </div>
      </div>

      {/* View mode tabs */}
      <div className={classes.TabRow}>
        <button
          className={`${classes.Tab} ${viewMode === "table" ? classes.TabActive : ""}`}
          onClick={() => setViewMode("table")}
        >
          Taulukko
        </button>
        <button
          className={`${classes.Tab} ${viewMode === "pipeline" ? classes.TabActive : ""}`}
          onClick={() => setViewMode("pipeline")}
        >
          Myyntiputki
        </button>
      </div>

      {/* Filters */}
      <div className={classes.FilterRow}>
        <button
          className={`${classes.FilterBtn} ${statusFilter === "ALL" ? classes.FilterBtnActive : ""}`}
          onClick={() => setStatusFilter("ALL")}
        >
          Kaikki
        </button>
        {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => (
          <button
            key={key}
            className={`${classes.FilterBtn} ${statusFilter === key ? classes.FilterBtnActive : ""}`}
            onClick={() => setStatusFilter(key as LeadStatus)}
          >
            {label}
          </button>
        ))}
        <input
          className={classes.SearchInput}
          type="text"
          placeholder="Hae nimellä, kaupungilla, Y-tunnuksella..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {error && <p style={{ color: "#dc2626" }}>{error}</p>}

      {/* TABLE VIEW */}
      {viewMode === "table" && (
        <>
          {filteredLeads.length === 0 ? (
            <div className={classes.EmptyState}>Ei liidejä{statusFilter !== "ALL" ? " tällä statuksella" : ""}</div>
          ) : (
            <table className={classes.Table}>
              <thead>
                <tr>
                  <th style={{ width: "40px", textAlign: "center" }}>
                    <input
                      type="checkbox"
                      checked={filteredLeads.length > 0 && selectedLeadIds.length === filteredLeads.length}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedLeadIds(filteredLeads.map(l => l.id));
                        } else {
                          setSelectedLeadIds([]);
                        }
                      }}
                    />
                  </th>
                  <th>Yritys</th>
                  <th>Y-tunnus</th>
                  <th>Kaupunki</th>
                  <th>Toimiala</th>
                  <th>Status</th>
                  <th>Lisätty palveluntarjoajaksi</th>
                  <th>Kontaktointi</th>
                  <th>Luotu</th>
                </tr>
              </thead>
              <tbody>
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} onClick={() => openDrawer(lead)}>
                    <td style={{ textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedLeadIds.includes(lead.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedLeadIds(prev => [...prev, lead.id]);
                          } else {
                            setSelectedLeadIds(prev => prev.filter(id => id !== lead.id));
                          }
                        }}
                      />
                    </td>
                    <td>
                      <strong>{lead.ytjName || lead.placeName || "-"}</strong>
                      {lead.isToiminimi && (
                        <span className={classes.ToiminimiBadge} style={{ marginLeft: "0.5rem" }}>
                          Tmi
                        </span>
                      )}
                      {lead.source && (
                        <span className={`${classes.SourceBadge} ${lead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`}>
                          {lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                        </span>
                      )}
                    </td>
                    <td>{lead.businessId || "-"}</td>
                    <td>{lead.city || "-"}</td>
                    <td>{lead.businessLine || "-"}</td>
                    <td>
                      <span className={`${classes.StatusBadge} ${statusBadgeClass(lead.status)}`}>
                        {LEAD_STATUS_LABELS[lead.status] || lead.status}
                      </span>
                    </td>
                    <td>
                      {lead.convertedToProvider ? (
                        <span style={{ color: "#16a34a", fontWeight: 500 }}>
                          <FontAwesomeIcon icon={faCheck} style={{ marginRight: "0.3rem" }} />
                          {lead.convertedToProviderDate ? formatDate(lead.convertedToProviderDate) : "Kyllä"}
                        </span>
                      ) : (
                        <span style={{ color: "#94a3b8" }}>-</span>
                      )}
                    </td>
                    <td>{CONTACT_METHOD_LABELS[lead.contactMethod || ""] || lead.contactMethod || "-"}</td>
                    <td>{formatDateTime(lead.created)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}

      {/* PIPELINE VIEW */}
      {viewMode === "pipeline" && (
        <div className={classes.Board}>
          {pipelineColumns
            .filter((col) => statusFilter === "ALL" || statusFilter === col.status)
            .map((col) => (
            <div
              className={classes.Column}
              key={col.status}
              onDragOver={(e) => e.preventDefault()}
              onDrop={async (e) => {
                e.preventDefault();
                const leadId = e.dataTransfer.getData("text/plain");
                if (!leadId) return;
                const lead = leads.find((l) => l.id === leadId);
                if (!lead || lead.status === col.status) return;
                const previousStatus = lead.status;
                // Optimistic UI update
                setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: col.status } : l)));
                try {
                  const updated = await LeadService.updateLead(leadId, { status: col.status });
                  setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, ...updated } : l)));
                } catch (err) {
                  console.error("DnD status update failed:", err);
                  // Rollback
                  setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status: previousStatus } : l)));
                  setError("Virhe tilan päivityksessä raahauksen jälkeen.");
                }
                setDraggedLeadId(null);
              }}
            >
              <div className={classes.ColHeader}>
                <h3 style={{ color: col.color }}>{col.label}</h3>
                <span className={classes.CountBadge}>{leadsByStatus[col.status]?.length || 0}</span>
              </div>
              <div className={classes.CardList}>
                {(leadsByStatus[col.status] || []).length === 0 ? (
                  <div className={classes.EmptyColumn}>Ei liidejä</div>
                ) : (
                  (leadsByStatus[col.status] || []).map((lead) => (
                    <div
                      className={`${classes.Card} ${draggedLeadId === lead.id ? classes.CardDragging : ""} ${searchQuery.trim() && !isSearchMatch(lead) ? classes.CardDimmed : ""} ${searchQuery.trim() && isSearchMatch(lead) ? classes.CardHighlighted : ""}`}
                      key={lead.id}
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", lead.id);
                        setDraggedLeadId(lead.id);
                      }}
                      onDragEnd={() => setDraggedLeadId(null)}
                      onClick={() => openDrawer(lead)}
                    >
                      <div className={classes.CardName}>{lead.ytjName || lead.placeName || "-"}</div>
                      <div className={classes.CardMeta}>
                        {lead.city && <span>{lead.city}</span>}
                        {lead.businessLine && <span>{lead.businessLine}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {lead.isToiminimi && (
                          <span className={classes.ToiminimiBadge} style={{ width: "fit-content" }}>
                            Tmi
                          </span>
                        )}
                        {lead.source && (
                          <span className={`${classes.SourceBadge} ${lead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`} style={{ width: 'fit-content', marginLeft: 0 }}>
                            {lead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                          </span>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* DRAWER */}
      {drawerOpen && selectedLead && (
        <div className={classes.DrawerOverlay} onClick={closeDrawer}>
          <div className={classes.Drawer} onClick={(e) => e.stopPropagation()}>
            <div className={classes.DrawerHeader}>
              <div>
                <h4>{selectedLead.ytjName || selectedLead.placeName || "Liidi"}</h4>
                <div className={classes.DrawerSub}>
                  {selectedLead.businessId && <span>Y-tunnus: {selectedLead.businessId}</span>}
                  {selectedLead.companyForm && <span> · {selectedLead.companyForm}</span>}
                  {selectedLead.source && (
                    <span className={`${classes.SourceBadge} ${selectedLead.source === 'OUTREACH_BATCH' ? classes.SourceBatch : classes.SourceManual}`}>
                      {selectedLead.source === 'OUTREACH_BATCH' ? 'Taustahaku' : 'Manuaalinen'}
                    </span>
                  )}
                </div>
              </div>
              <button className={classes.CloseBtn} onClick={closeDrawer}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div className={classes.DrawerScroll}>
              {/* Company info */}
              <div className={classes.SectionBox}>
                <h5>Yrityksen tiedot</h5>
                <dl className={classes.DetailGrid}>
                  {selectedLead.ytjName && <><dt>YTJ-nimi</dt><dd>{selectedLead.ytjName}</dd></>}
                  {selectedLead.businessId && <><dt>Y-tunnus</dt><dd>{selectedLead.businessId}</dd></>}
                  {selectedLead.companyForm && <><dt>Yhtiömuoto</dt><dd>{selectedLead.companyForm}</dd></>}
                  {selectedLead.businessLine && <><dt>Toimiala</dt><dd>{selectedLead.businessLine}</dd></>}
                  {selectedLead.street && <><dt>Osoite</dt><dd>{selectedLead.street}, {selectedLead.postCode} {selectedLead.city}</dd></>}
                  {selectedLead.phone && <><dt>Puhelin</dt><dd>{selectedLead.phone}</dd></>}
                  {selectedLead.email && <><dt>Sähköposti</dt><dd>{selectedLead.email}</dd></>}
                  {selectedLead.website && <><dt>Verkkosivut</dt><dd><a href={selectedLead.website.startsWith("http") ? selectedLead.website : `https://${selectedLead.website}`} target="_blank" rel="noopener noreferrer">{selectedLead.website}</a></dd></>}
                  {selectedLead.contactPerson && <><dt>Yhteyshenkilö</dt><dd>{selectedLead.contactPerson}</dd></>}
                  {selectedLead.description && (
                    <>
                      <dt>AI-kuvaus</dt>
                      <dd style={{ fontStyle: 'italic', color: '#475569', lineHeight: 1.5 }}>{selectedLead.description}</dd>
                    </>
                  )}
                </dl>
                {selectedLead.placeId && (
                  <div style={{ marginTop: "0.75rem" }}>
                    <a
                      href={`https://www.google.com/maps/place/?q=place_id:${selectedLead.placeId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontSize: "0.85rem", color: "#2563eb" }}
                    >
                      <FontAwesomeIcon icon={faExternalLinkAlt} /> Avaa Google Mapsissa
                    </a>
                  </div>
                )}
              </div>

              {/* Status */}
              <div className={classes.SectionBox}>
                <h5>Status{updatingStatus && <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: "0.5rem", fontSize: "0.8rem" }} />}</h5>
                <div className={classes.StateSelectorRow}>
                  {Object.entries(LEAD_STATUS_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      className={`${classes.StateBtn} ${selectedLead.status === key ? classes.StateBtnActive : ""}`}
                      onClick={() => handleStatusChange(key as LeadStatus)}
                      disabled={updatingStatus}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Muistiinpanot ja Aktiviteetit */}
              <div className={classes.SectionBox}>
                <h5>
                  <FontAwesomeIcon icon={faClock} style={{ marginRight: "0.5rem", color: "#2563eb" }} />
                  Muistiinpanot & Aktiviteetit {timelineItems.length > 0 && <span style={{ color: "#64748b", fontWeight: "normal", fontSize: "0.85rem" }}>({timelineItems.length})</span>}
                </h5>

                {/* New Note Composer */}
                <div className={classes.ActivityComposer} style={{ marginTop: "0.5rem" }}>
                  <div className={classes.ComposerHeader}>
                    <span className={classes.ComposerTitle}>
                      <FontAwesomeIcon icon={faCommentAlt} style={{ marginRight: "0.4rem", color: "#2563eb" }} />
                      Kirjaa uusi muistiinpano
                    </span>
                    {selectedLead.serviceToken && (
                      <span className={classes.ComposerServiceTag}>
                        Palvelu: {services?.find((s) => s.serviceToken === selectedLead.serviceToken)?.name || selectedLead.serviceToken}
                      </span>
                    )}
                  </div>
                  <textarea
                    className={classes.ComposerTextarea}
                    placeholder="Kirjoita muistiinpano... (Paina Enter tallentaaksesi, Shift+Enter rivinvaihtoon)"
                    value={newNote}
                    rows={2}
                    onChange={(e) => setNewNote(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        handleAddNote();
                      }
                    }}
                    disabled={savingNote}
                  />
                  <div className={classes.ComposerFooter}>
                    <span className={classes.ComposerHint}>Enter tallentaa · Shift+Enter rivinvaihto</span>
                    <Button
                      onClick={handleAddNote}
                      disabled={savingNote || !newNote.trim()}
                      style={{ padding: "0.35rem 1rem", fontSize: "0.85rem" }}
                    >
                      {savingNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Lisää muistiinpano"}
                    </Button>
                  </div>
                </div>

                {/* Filter Pills */}
                <div className={classes.ActivityFilterRow} style={{ marginTop: "0.75rem" }}>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "ALL" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("ALL")}
                  >
                    Kaikki ({timelineItems.length})
                  </button>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "NOTES" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("NOTES")}
                  >
                    Muistiinpanot ({notes.length})
                  </button>
                  <button
                    className={`${classes.ActivityFilterBtn} ${activityFilter === "CHANGES" ? classes.ActivityFilterBtnActive : ""}`}
                    onClick={() => setActivityFilter("CHANGES")}
                  >
                    Muutokset & Lokit ({timelineItems.length - notes.length})
                  </button>
                </div>

                {/* Timeline */}
                {loadingNotes ? (
                  <div style={{ textAlign: "center", padding: "2rem" }}><Spinner /></div>
                ) : filteredTimelineItems.length === 0 ? (
                  <div className={classes.EmptyTimeline}>
                    Ei näytettäviä tapahtumia valitulla suodatuksella.
                  </div>
                ) : (
                  <div className={classes.Timeline}>
                    {filteredTimelineItems.map((item) => (
                      <div className={classes.TimelineItem} key={item.id}>
                        {/* Left Icon Badge */}
                        <div
                          className={`${classes.TimelineIconTrack} ${
                            item.type === "NOTE"
                              ? classes.TimelineIconNote
                              : item.type === "STATUS_CHANGE"
                              ? classes.TimelineIconStatus
                              : item.type === "EMAIL_SENT"
                              ? classes.TimelineIconEmail
                              : item.type === "CONVERTED"
                              ? classes.TimelineIconConvert
                              : item.type === "CREATED"
                              ? classes.TimelineIconCreate
                              : classes.TimelineIconNote
                          }`}
                        >
                          {item.type === "NOTE" && <FontAwesomeIcon icon={faCommentAlt} />}
                          {item.type === "STATUS_CHANGE" && <FontAwesomeIcon icon={faSyncAlt} />}
                          {item.type === "EMAIL_SENT" && <FontAwesomeIcon icon={faPaperPlane} />}
                          {item.type === "CONVERTED" && <FontAwesomeIcon icon={faUserPlus} />}
                          {item.type === "CREATED" && <FontAwesomeIcon icon={faPlusCircle} />}
                          {(item.type === "UPDATE" || item.type === "SYSTEM") && <FontAwesomeIcon icon={faInfoCircle} />}
                        </div>

                        {/* Event Card */}
                        <div className={classes.TimelineCard}>
                          <div className={classes.TimelineHeader}>
                            <div className={classes.TimelineAuthorMeta}>
                              {item.type === "NOTE" && (
                                <div className={classes.AvatarCircle}>
                                  {getInitials(item.authorName)}
                                </div>
                              )}
                              <span className={classes.TimelineAuthorName}>{item.authorName || item.title}</span>
                              <span>·</span>
                              <span className={classes.TimelineDate} title={formatDateTime(item.timestamp)}>
                                {timeAgo(item.timestamp)}
                              </span>
                              {item.serviceName && (
                                <span className={classes.ServiceBadge}>{item.serviceName}</span>
                              )}
                            </div>

                            {item.type === "NOTE" && item.noteId && (
                              <div className={classes.TimelineActions}>
                                <button
                                  className={classes.ItemEditBtn}
                                  title="Muokkaa muistiinpanoa"
                                  onClick={() => handleStartEditNote(item.rawNote!)}
                                >
                                  <FontAwesomeIcon icon={faPencilAlt} />
                                </button>
                                <button
                                  className={classes.ItemDeleteBtn}
                                  title="Poista muistiinpano"
                                  onClick={() => handleDeleteNote(item.noteId!)}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                </button>
                              </div>
                            )}
                          </div>

                          {/* Note inline editing mode */}
                          {item.type === "NOTE" && editingNoteId === item.noteId ? (
                            <div className={classes.EditNoteBox}>
                              <textarea
                                value={editingNoteContent}
                                onChange={(e) => setEditingNoteContent(e.target.value)}
                                rows={2}
                                disabled={savingEditNote}
                              />
                              <div className={classes.EditActions}>
                                <button
                                  onClick={handleCancelEditNote}
                                  style={{
                                    background: "none",
                                    border: "1px solid #cbd5e1",
                                    padding: "0.2rem 0.6rem",
                                    borderRadius: "4px",
                                    cursor: "pointer",
                                    fontSize: "0.78rem",
                                  }}
                                >
                                  Peruuta
                                </button>
                                <Button
                                  onClick={() => handleSaveEditNote(item.noteId!)}
                                  disabled={savingEditNote || !editingNoteContent.trim()}
                                  style={{ padding: "0.2rem 0.6rem", fontSize: "0.78rem" }}
                                >
                                  {savingEditNote ? <FontAwesomeIcon icon={faSpinner} spin /> : "Tallenna"}
                                </Button>
                              </div>
                            </div>
                          ) : item.type === "STATUS_CHANGE" ? (
                            <div className={classes.StatusChangeFlow}>
                              <span className={classes.StatusOldBadge}>
                                {item.oldLabel || item.oldValue || "Ei tilaa"}
                              </span>
                              <span className={classes.StatusArrow}>➔</span>
                              <span className={classes.StatusNewBadge}>
                                {item.newLabel || item.newValue}
                              </span>
                            </div>
                          ) : (
                            <div className={classes.TimelineContent}>
                              {item.description || item.title}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Email */}
              {(selectedLead.generatedEmailSubject || selectedLead.generatedEmailBody) && (
                <div className={classes.SectionBox}>
                  <h5>
                    <FontAwesomeIcon icon={faEnvelope} style={{ marginRight: "0.5rem" }} />
                    Generoitu sähköposti
                  </h5>
                  <div className={classes.EmailBox}>
                    <div className={classes.EmailSubject}>{selectedLead.generatedEmailSubject || "-"}</div>
                    <div className={classes.EmailBody}>{selectedLead.generatedEmailBody || "-"}</div>
                  </div>
                  <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.75rem", flexWrap: "wrap" }}>
                    <Button onClick={handleCopyEmail}>
                      <FontAwesomeIcon icon={faCopy} /> Kopioi leikepöydälle
                    </Button>
                    {!selectedLead.isToiminimi && selectedLead.email && (
                      <Button onClick={handleSendEmail} disabled={sendingEmail}>
                        {sendingEmail ? (
                          <><FontAwesomeIcon icon={faSpinner} spin /> Lähetetään...</>
                        ) : (
                          <><FontAwesomeIcon icon={faPaperPlane} /> Lähetä MailGunilla</>
                        )}
                      </Button>
                    )}
                  </div>
                  {selectedLead.emailSentAt && (
                    <div className={classes.EmailSentBadge}>
                      <FontAwesomeIcon icon={faCheck} />
                      Lähetetty {formatDateTime(selectedLead.emailSentAt)}
                    </div>
                  )}
                  {emailSent && !selectedLead.emailSentAt && (
                    <div className={classes.EmailSentBadge}>
                      <FontAwesomeIcon icon={faCheck} />
                      Sähköposti lähetetty!
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className={classes.DrawerFooter}>
              <button className={classes.DeleteBtn} onClick={handleDelete} disabled={deletingLead}>
                <FontAwesomeIcon icon={faTrash} /> {deletingLead ? "Poistetaan..." : "Poista liidi"}
              </button>
              <Button onClick={closeDrawer}>Sulje</Button>
            </div>
          </div>
        </div>
      )}
      {/* CONFIRM DIALOG */}
      {showConfirm && (
        <div className={classes.DrawerOverlay} onClick={() => setShowConfirm(false)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "2rem", maxWidth: "480px", margin: "10vh auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Vahvista konversio</h3>
            <p>Olet lisäämässä <strong>{selectedLeadIds.length} liidiä</strong> palveluntarjoajiksi.</p>
            <p style={{ color: "#64748b", fontSize: "0.9rem" }}>Duplikaatit (sama nimi) ohitetaan automaattisesti. Jo konvertoidut liidit ohitetaan.</p>
            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end", marginTop: "1.5rem" }}>
              <button onClick={() => setShowConfirm(false)} style={{ padding: "0.5rem 1rem", border: "1px solid #d1d5db", borderRadius: "6px", background: "white", cursor: "pointer" }}>Peruuta</button>
              <Button onClick={async () => {
                setShowConfirm(false);
                setConverting(true);
                try {
                  const result = await LeadService.bulkConvertToProviders(selectedServiceToken, selectedLeadIds);
                  setConvertResult(result);
                  setSelectedLeadIds([]);
                  await fetchLeads(); // Refresh leads to get convertedToProvider flags
                } catch (err: any) {
                  setError("Konversio epäonnistui: " + err.message);
                } finally {
                  setConverting(false);
                }
              }}>
                <FontAwesomeIcon icon={faUserPlus} style={{ marginRight: "0.4rem" }} />
                Lisää palveluntarjoajiksi
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* CONVERT RESULT MODAL */}
      {convertResult && (
        <div className={classes.DrawerOverlay} onClick={() => setConvertResult(null)}>
          <div style={{ background: "white", borderRadius: "12px", padding: "2rem", maxWidth: "600px", margin: "10vh auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)", maxHeight: "70vh", overflow: "auto" }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Konversion tulos</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "1rem", marginBottom: "1.5rem" }}>
              <div style={{ background: "#f0fdf4", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#16a34a" }}>{convertResult.created}</div>
                <div style={{ color: "#16a34a", fontSize: "0.85rem" }}>Luotu</div>
              </div>
              <div style={{ background: "#fefce8", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                <div style={{ fontSize: "2rem", fontWeight: 700, color: "#ca8a04" }}>{convertResult.skipped + convertResult.alreadyConverted}</div>
                <div style={{ color: "#ca8a04", fontSize: "0.85rem" }}>Ohitettu</div>
              </div>
              {convertResult.errors > 0 && (
                <div style={{ background: "#fef2f2", padding: "1rem", borderRadius: "8px", textAlign: "center" }}>
                  <div style={{ fontSize: "2rem", fontWeight: 700, color: "#dc2626" }}>{convertResult.errors}</div>
                  <div style={{ color: "#dc2626", fontSize: "0.85rem" }}>Virhe</div>
                </div>
              )}
            </div>
            {convertResult.details && convertResult.details.length > 0 && (
              <details>
                <summary style={{ cursor: "pointer", color: "#64748b", marginBottom: "0.5rem" }}>Näytä yksityiskohdat ({convertResult.details.length})</summary>
                <div style={{ maxHeight: "300px", overflow: "auto", fontSize: "0.85rem" }}>
                  {convertResult.details.map((d, i) => (
                    <div key={i} style={{ padding: "0.25rem 0", borderBottom: "1px solid #f1f5f9" }}>
                      <span style={{ color: d.status === 'CREATED' ? '#16a34a' : d.status === 'ERROR' ? '#dc2626' : '#ca8a04' }}>
                        {d.status === 'CREATED' ? '✅' : d.status === 'ERROR' ? '❌' : '⏭️'}
                      </span>
                      {' '}{d.name || d.leadId} {d.reason ? `— ${d.reason}` : ''}
                    </div>
                  ))}
                </div>
              </details>
            )}
            <div style={{ textAlign: "right", marginTop: "1rem" }}>
              <Button onClick={() => setConvertResult(null)}>Sulje</Button>
            </div>
          </div>
        </div>
      )}

      {/* CONVERTING OVERLAY */}
      {converting && (
        <div className={classes.DrawerOverlay} style={{ zIndex: 1100 }}>
          <div style={{ background: "white", borderRadius: "12px", padding: "3rem", maxWidth: "400px", margin: "20vh auto", textAlign: "center", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
            <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "2rem", color: "#2563eb", marginBottom: "1rem" }} />
            <p style={{ fontSize: "1.1rem", fontWeight: 500 }}>Lisätään palveluntarjoajia...</p>
            <p style={{ color: "#64748b", fontSize: "0.9rem" }}>Tämä voi kestää hetken.</p>
          </div>
        </div>
      )}
    </Container>
  );
};

export default Leads;
